import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography
} from "@mui/material";
import TrashIcon from "../../icons/TrashIcon";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import UndoIcon from "@mui/icons-material/Undo";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import TextDecreaseIcon from "@mui/icons-material/TextDecrease";
import { useTranslation } from "react-i18next";
import { ClinicalRecord, Privacy } from "../../hooks/useClinicalRecords";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import ToolbarBtn from "./ToolbarBtn";

interface Props {
  formView: "add" | "edit";
  isNotes: boolean;
  editing: ClinicalRecord | null;
  privacyOptions: Array<{ value: Privacy; label: string }>;
  onSubmit: (
    content: string,
    privacy: Privacy,
    visibleToPatient: boolean
  ) => void;
  onCancel: () => void;
  onDeleteRequest: () => void;
}

export default function RecordEditor({
  formView,
  isNotes,
  editing,
  privacyOptions,
  onSubmit,
  onCancel,
  onDeleteRequest
}: Props) {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const [privacy, setPrivacy] = useState<Privacy>("only-me");
  const [visibleToPatient, setVisibleToPatient] = useState(false);
  const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>(
    {}
  );

  const updateFormatState = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList")
    });
  };

  useEffect(() => {
    document.addEventListener("selectionchange", updateFormatState);
    return () =>
      document.removeEventListener("selectionchange", updateFormatState);
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    editor.innerHTML = editing?.content ?? "";
    editor.focus();
    // focus() alone drops the caret at the start of the field (top-left), which
    // looks like the text begins above where it actually is. Collapse the
    // selection to the end so the caret sits right after the existing content.
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    if (formView === "edit" && editing) {
      setPrivacy(editing.privacy);
      setVisibleToPatient(editing.visibleToPatient);
    } else {
      setPrivacy("only-me");
      setVisibleToPatient(false);
    }
  }, [formView, editing]);

  const exec = (cmd: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, undefined);
    updateFormatState();
  };

  const execFontSize = (increase: boolean) => {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const container = range.startContainer;
    const el =
      container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : (container as HTMLElement);
    const currentPx = el
      ? parseFloat(window.getComputedStyle(el).fontSize)
      : 16;
    const STEP = 4;
    const newPx = increase
      ? Math.min(Math.round(currentPx) + STEP, 72)
      : Math.max(Math.round(currentPx) - STEP, 8);

    const span = document.createElement("span");
    span.style.fontSize = `${newPx}px`;

    if (selection.isCollapsed) {
      // No selection: insert an invisible anchor span so the next typed
      // characters inherit the new font size
      span.appendChild(document.createTextNode("​"));
      range.insertNode(span);
      const newRange = document.createRange();
      newRange.setStart(span.firstChild as Text, 1);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      try {
        range.surroundContents(span);
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch {
        // selection spans multiple block elements — fall back to execCommand
        document.execCommand("styleWithCSS", false, "true");
        const levels = [8, 10, 13, 16, 18, 24, 32, 48, 72];
        const idx = levels.findIndex(s => s >= newPx);
        const htmlSize = String(Math.max(1, Math.min(7, idx)));
        document.execCommand("fontSize", false, htmlSize);
      }
    }
  };

  const handleSubmit = () => {
    const content = editorRef.current?.innerHTML?.trim() ?? "";
    if (!content || content === "<br>") return;
    onSubmit(content, privacy, visibleToPatient);
  };

  return (
    <Box
      sx={{
        bgcolor: semanticColors.surface.canvas,
        borderRadius: "20px",
        boxShadow: "0px 0px 8px rgba(30,39,79,0.10)",
        p: "24px"
      }}
    >
      <Typography
        sx={{
          ...typography.heading.s,
          color: semanticColors.text.primary,
          mb: 3
        }}
      >
        {formView === "add"
          ? isNotes
            ? t("clinicalRecords.newNote")
            : t("clinicalRecords.newSummary")
          : isNotes
          ? t("clinicalRecords.editNote")
          : t("clinicalRecords.editSummary")}
      </Typography>

      {/* Privacy + visible toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 3,
          mb: 3,
          flexWrap: "wrap"
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{ ...typography.body.m, color: semanticColors.text.secondary }}
          >
            {t("clinicalRecords.privacy")}
          </Typography>
          <Select
            value={privacy}
            onChange={e => setPrivacy(e.target.value as Privacy)}
            size="small"
            sx={{
              minWidth: 210,
              borderRadius: "8px",
              ...typography.body.l,
              color: semanticColors.text.primary,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: semanticColors.border.strong
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: semanticColors.text.primary
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: semanticColors.border.focus
              }
            }}
          >
            {privacyOptions.map(({ value, label }) => (
              <MenuItem
                key={value}
                value={value}
                sx={{
                  ...typography.body.l,
                  color: semanticColors.text.primary
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={visibleToPatient}
              onChange={e => setVisibleToPatient(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: semanticColors.action.primary
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: semanticColors.action.primary
                }
              }}
            />
          }
          label={
            <Typography
              sx={{ ...typography.body.m, color: semanticColors.text.primary }}
            >
              {t("clinicalRecords.visibleToPatient")}
            </Typography>
          }
        />
      </Box>

      {/* Editor */}
      <Box
        sx={{
          border: `1.5px solid ${semanticColors.border.strong}`,
          borderRadius: "12px",
          overflow: "hidden",
          mb: 3
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: "8px",
            py: "6px",
            borderBottom: `1px solid ${semanticColors.border.middle}`,
            bgcolor: semanticColors.surface.containerLow,
            flexWrap: "wrap"
          }}
        >
          <ToolbarBtn
            active={activeFormats.bold}
            onClick={() => exec("bold")}
            title={t("clinicalRecords.bold")}
          >
            <FormatBoldIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <ToolbarBtn
            active={activeFormats.italic}
            onClick={() => exec("italic")}
            title={t("clinicalRecords.italic")}
          >
            <FormatItalicIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <ToolbarBtn
            active={activeFormats.underline}
            onClick={() => exec("underline")}
            title={t("clinicalRecords.underline")}
          >
            <FormatUnderlinedIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <ToolbarBtn
            active={activeFormats.strikeThrough}
            onClick={() => exec("strikeThrough")}
            title={t("clinicalRecords.strikethrough")}
          >
            <StrikethroughSIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, my: "4px" }}
          />
          <ToolbarBtn
            active={activeFormats.insertUnorderedList}
            onClick={() => exec("insertUnorderedList")}
            title={t("clinicalRecords.bulletList")}
          >
            <FormatListBulletedIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <ToolbarBtn
            active={activeFormats.insertOrderedList}
            onClick={() => exec("insertOrderedList")}
            title={t("clinicalRecords.numberedList")}
          >
            <FormatListNumberedIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, my: "4px" }}
          />
          <ToolbarBtn
            onClick={() => exec("undo")}
            title={t("clinicalRecords.undo")}
          >
            <UndoIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, my: "4px" }}
          />
          <ToolbarBtn
            onClick={() => execFontSize(true)}
            title={t("clinicalRecords.increaseFontSize")}
          >
            <TextIncreaseIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => execFontSize(false)}
            title={t("clinicalRecords.decreaseFontSize")}
          >
            <TextDecreaseIcon sx={{ fontSize: 18 }} />
          </ToolbarBtn>
        </Box>

        {/* Editable area */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={
            isNotes
              ? t("clinicalRecords.placeholder")
              : t("clinicalRecords.placeholderSummary")
          }
          sx={{
            minHeight: "220px",
            p: "16px",
            outline: "none",
            ...typography.body.l,
            color: semanticColors.text.primary,
            "&:empty:before": {
              content: "attr(data-placeholder)",
              color: semanticColors.text.subtle,
              pointerEvents: "none"
            }
          }}
        />
      </Box>

      {/* Form actions */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        {formView === "edit" && (
          <Tooltip title={t("clinicalRecords.delete")}>
            <IconButton
              onClick={onDeleteRequest}
              sx={{ color: semanticColors.feedback.danger }}
            >
              <TrashIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={onCancel}
          sx={{
            px: "24px",
            height: "48px",
            borderRadius: "40px",
            border: `1.5px solid ${semanticColors.border.strong}`,
            bgcolor: "transparent",
            cursor: "pointer",
            ...typography.body.lBold,
            color: semanticColors.text.primary,
            "&:hover": { bgcolor: semanticColors.surface.containerLow }
          }}
        >
          {t("clinicalRecords.cancel")}
        </Box>
        <Box
          component="button"
          onClick={handleSubmit}
          sx={{
            px: "24px",
            height: "48px",
            borderRadius: "40px",
            border: "none",
            background: semanticColors.gradient.surface,
            cursor: "pointer",
            ...typography.body.lBold,
            color: semanticColors.text.inverse
          }}
        >
          {formView === "add"
            ? t("clinicalRecords.save")
            : t("clinicalRecords.update")}
        </Box>
      </Box>
    </Box>
  );
}
